import Button from "@/components/bricks/button/Button";
import { useContext, useEffect, useState } from "react";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import InputRadio from "../../formcontrols/InputRadio";
import { FormikTextArea } from "../../formcontrols/TextArea";
import { ModalContext } from "../../Modal/Modal";
import {
  TicketHeading,
  TicketRoot,
  TicketSub,
  TicketSubItem,
} from "../../ticket/Ticket";
import { WizardReceipt } from "../common/wizardReceipt";
import { useDeponerAdvokat } from "./useDeponerAdvokat";
import {
  dateIsWithinInterval,
  periodeIsActive,
  stringDateToPP,
} from "../../../util/date";
import {
  FirmaerConnection,
  Maybe,
  PersonDto,
  PersonFirma,
  PersonPrincipal,
} from "../../../interfaces/schema";
import { AlertMessage } from "../../AlertMessage";
import { extractCurrentPeronBeskikkelse } from "../../../util/person";
import { useQuery } from "@apollo/client";
import { GET_FIRMAER_BY_IDS_INCLUDE_PERSONRELATIONER } from "@/api/firma/queries";

export default function DeponerAdvokatWizard({
  person,
  invalidateData,
}: {
  person: PersonDto;
  invalidateData: () => void;
}) {
  const [receiptHeading, setReceiptHeading] = useState("");
  const currentBeskikkelse = extractCurrentPeronBeskikkelse(person);
  const beskikkelsesNr = currentBeskikkelse?.beskikkelsestypeNavigation?.nr;
  const frakendOnly =
    beskikkelsesNr === "DEPONERET" ||
    beskikkelsesNr === "AFREGISTRERET_EU_ADVOKAT";
  const { formik, afslutBeskikkelseQuery } = useDeponerAdvokat(
    person,
    setReceiptHeading,
  );
  const { handleSubmit, setFieldValue } = formik;
  useEffect(() => {
    if (frakendOnly) {
      setFieldValue("skalFrakendes", true, true);
      setFieldValue("skalDeponeres", false, true);
    }
  }, [frakendOnly, setFieldValue]);
  const { data, error, isPending } = afslutBeskikkelseQuery;
  const { setModalOpen } = useContext(ModalContext);
  const { fraDato } = formik.values;
  const fuldmaegtigeAtStartDate = fraDato
    ? person.personPrincipalPersonPrincipalNavigation?.filter((f) => {
        return dateIsWithinInterval(
          fraDato,
          new Date(f.periode.fraDato),
          new Date(f.periode.tilDatoInklusiv),
        );
      })
    : [];
  const personFirmaEjer = person.personFirma
    ? person.personFirma.filter(
        (f) =>
          f.personfirmatypeNrNavigation?.nr === "EJER" &&
          periodeIsActive(
            new Date(f.periode.fraDato),
            new Date(f.periode.tilDatoInklusiv),
          ),
      )
    : [];
  const currentPersonFirmas = person.personFirma?.filter((f) =>
    periodeIsActive(
      new Date(f.periode.fraDato),
      new Date(f.periode.tilDatoInklusiv),
    ),
  );

  const { data: firmaData } = useQuery<{
    firmaer: FirmaerConnection;
  }>(
    GET_FIRMAER_BY_IDS_INCLUDE_PERSONRELATIONER(
      currentPersonFirmas?.map((f) => f.firma.id) || [],
    ),
    {
      skip: !currentPersonFirmas || currentPersonFirmas.length === 0,
    },
  );

  function firmasWherePersonIsLast(
    firmaData: { firmaer: FirmaerConnection } | undefined,
    person: PersonDto,
  ) {
    const firmas =
      firmaData?.firmaer.edges?.map((firma) => {
        return {
          id: firma.node.id,
          navn: firma.node.navn1,
          personFirma: firma.node.personFirma?.filter(
            (pf) =>
              pf.periode &&
              periodeIsActive(
                new Date(pf.periode.fraDato),
                new Date(pf.periode.tilDatoInklusiv),
              ),
          ),
        };
      }) ?? [];
    return firmas
      .filter(
        (firma) =>
          firma.personFirma?.length === 1 &&
          firma.personFirma[0].person.id === person.id,
      )
      .map((firma) => {
        return {
          ...firma,
          personFirma: firma.personFirma![0]!,
        };
      });
  }

  if (error) {
    return <ReceiptError error={error} />;
  }
  if (data) {
    return (
      <Receipt
        fuldmaegtigeAtStartDate={fuldmaegtigeAtStartDate}
        receiptHeading={receiptHeading}
        invalidateData={invalidateData}
      />
    );
  }
  return (
    <div className="c-modal-container">
      <form
        className="c-item-list c-modal-container-inner"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <fieldset className="flex-100" disabled={isPending}>
          <legend className="form-label">Handling</legend>
          <div className="w-100 pt-2">
            <div
              className="form-check form-check-inline"
              style={{ paddingLeft: ".9rem" }}
            >
              <InputRadio
                label="Deponering"
                id="skalDeponeres"
                name="deponerFrakend"
                checked={
                  frakendOnly
                    ? false
                    : formik.values.skalDeponeres
                      ? true
                      : false
                }
                onChange={() => {
                  setFieldValue("skalDeponeres", true, true);
                  setFieldValue("skalFrakendes", false, true);
                }}
                disabled={frakendOnly}
              />
            </div>
            <div className="form-check form-check-inline">
              <InputRadio
                label="Frakendelse"
                id="skalFrakendes"
                name="deponerFrakend"
                checked={
                  frakendOnly
                    ? true
                    : formik.values.skalFrakendes
                      ? true
                      : false
                }
                onChange={() => {
                  setFieldValue("skalFrakendes", true, true);
                  setFieldValue("skalDeponeres", false, true);
                }}
              />
            </div>
          </div>
        </fieldset>
        <div className="flex-100">
          <FormikDatePicker
            formik={formik}
            config={{
              component: "datepicker",
              id: "fraDato",
              label: "Startdato",
              isRequired: true,
              initialValue: "",
            }}
          />
        </div>
        <div className="flex-100">
          <FormikTextArea
            formik={formik}
            config={{
              component: "textarea",
              id: "aarsag",
              label: "Årsag",
              isRequired: formik.values.skalFrakendes,
              initialValue: "",
            }}
          />
        </div>
        <PersonFirmaMessage
          personFirmaEjer={personFirmaEjer}
          firmasWherePersonIsLast={firmasWherePersonIsLast(firmaData, person)}
        />
        <div className="flex-100 gap-2 flex-row justify-content-between mt-3">
          <Button
            color="secondary"
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Annuller
          </Button>
          <Button type="submit" color="primary" disabled={isPending}>
            {/* Deponer */}
            {formik.values.skalDeponeres ? "Deponer" : "Frakend"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const ReceiptError = (props: { error: any }) => {
  const { error } = props;
  const { setModalOpen } = useContext(ModalContext);
  return (
    <div className="c-modal-container">
      <WizardReceipt heading="Noget gik galt" icon="warning">
        <p className="text-muted text-center">
          {error ? (error as any).data?.Message : "Luk for at afslutte"}
        </p>
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Luk
        </Button>
      </div>
    </div>
  );
};
function Receipt({
  fuldmaegtigeAtStartDate,
  receiptHeading,
  invalidateData,
}: {
  fuldmaegtigeAtStartDate: PersonPrincipal[] | undefined;
  receiptHeading: string;
  invalidateData: () => void;
}) {
  const { setModalOpen } = useContext(ModalContext);
  useEffect(() => {
    invalidateData();
  }, []);

  return (
    <div className="c-modal-container">
      <WizardReceipt heading={receiptHeading} icon="success">
        <>
          {fuldmaegtigeAtStartDate && fuldmaegtigeAtStartDate?.length > 0 && (
            <AlertMessage warningIcon heading="Bemærk personrelation">
              <p>
                Personen er principal for{" "}
                <b>{fuldmaegtigeAtStartDate.length} </b>
                {fuldmaegtigeAtStartDate.length === 1
                  ? "fuldmægtig: "
                  : "fuldmægtige:"}
              </p>
              <>
                {fuldmaegtigeAtStartDate.map((p, index) => (
                  <TicketRoot
                    icon="arrow"
                    className="bg-white shadow-sm mb-4"
                    key={index}
                    onClick={() =>
                      window.open(
                        `/person/${p.personFuldm.id}`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <TicketHeading>
                      <h4>{p.personFuldm.fuldtNavn}</h4>
                    </TicketHeading>
                    <TicketSub>
                      <TicketSubItem>
                        Fra dato: {stringDateToPP(p.periode.fraDato)}
                      </TicketSubItem>
                      <TicketSubItem> | </TicketSubItem>
                      <TicketSubItem>
                        Til dato: {stringDateToPP(p.periode.tilDatoInklusiv)}
                      </TicketSubItem>
                    </TicketSub>
                  </TicketRoot>
                ))}
              </>
              <p>
                Husk at rydde op. Der er sendt en notifikation til din e-mail
                herom.
              </p>
            </AlertMessage>
          )}
        </>
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Luk
        </Button>
      </div>
    </div>
  );
}

function PersonFirmaMessage(props: {
  personFirmaEjer: PersonFirma[];
  firmasWherePersonIsLast: SimplePersonFirma[];
}) {
  const { personFirmaEjer, firmasWherePersonIsLast } = props;
  if (personFirmaEjer.length === 0 && firmasWherePersonIsLast.length === 0) {
    return null;
  }
  return (
    <AlertMessage type="info" warningIcon heading="Bemærk firmarelation">
      {personFirmaEjer.length > 0 && (
        <>
          <p>
            Personen er dags dato ejer for følgende
            {personFirmaEjer.length > 1 ? " firmaer" : " firma"}
          </p>
          <>
            {personFirmaEjer.map((p) => (
              <TicketRoot
                icon="arrow"
                className="bg-white shadow-sm mb-4"
                key={p.firmaId}
                onClick={() =>
                  window.open(
                    `/firma/${p.firma.id}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <TicketHeading>
                  <h4>{p.firma.navn1}</h4>
                </TicketHeading>
                <TicketSub>
                  <TicketSubItem>
                    Fra dato: {stringDateToPP(p.periode.fraDato)}
                  </TicketSubItem>
                  <TicketSubItem> | </TicketSubItem>
                  <TicketSubItem>
                    Til dato: {stringDateToPP(p.periode.tilDatoInklusiv)}
                  </TicketSubItem>
                </TicketSub>
              </TicketRoot>
            ))}
          </>
        </>
      )}
      {firmasWherePersonIsLast.length > 0 && (
        <>
          <p>
            Personen er dags dato eneste ansatte i følgende
            {firmasWherePersonIsLast.length > 1 ? " firmaer" : " firma"}
          </p>
          <>
            {firmasWherePersonIsLast.map((p) => (
              <TicketRoot
                icon="arrow"
                className="bg-white shadow-sm mb-4"
                key={p.id}
                onClick={() =>
                  window.open(`/firma/${p.id}`, "_blank", "noopener,noreferrer")
                }
              >
                <TicketHeading>
                  <h4>{p.navn}</h4>
                </TicketHeading>
                <TicketSub>
                  <TicketSubItem>
                    Fra dato: {stringDateToPP(p.personFirma?.periode.fraDato)}
                  </TicketSubItem>
                  <TicketSubItem> | </TicketSubItem>
                  <TicketSubItem>
                    Til dato:{" "}
                    {stringDateToPP(p.personFirma?.periode.tilDatoInklusiv)}
                  </TicketSubItem>
                </TicketSub>
              </TicketRoot>
            ))}
          </>
        </>
      )}
    </AlertMessage>
  );
}

type SimplePersonFirma = {
  id: string;
  navn: Maybe<string> | undefined;
  personFirma: PersonFirma;
};
